import React, { useRef, useState } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import classNames from 'classnames'
import { twMerge } from 'tailwind-merge'
import Field from '../Field'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import TurndownService from 'turndown'
import './MarkdownTextArea.css'

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    ['link'],
    ['clean'],
  ],
}

const formats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'link',
]

marked.setOptions({
  breaks: true,
})

// Preserve the author's line structure - a newline stays a newline, a blank
// line stays a blank line - while removing the Quill/Turndown round-trip
// artifacts that otherwise inflate spacing: trailing-space phantom lines from
// empty paragraphs, and runaway blank-line runs. Used on both the display path
// (below) and the edit path (handleChange) so the round-trip is stable and
// existing notes don't accumulate extra spacing each time they're saved.
const normalizeMarkdown = (markdown) => (markdown || '')
  .replace(/\r\n?/g, '\n')      // normalise CRLF/CR to LF first (else \r survives
                                //   into marked and inflates every line break)
  .replace(/[ \t]+$/gm, '')     // strip trailing-space hard-break artifacts
  .replace(/\n{3,}/g, '\n\n')   // cap blank-line runs at a single blank line
  .trim()

const convertMarkdownToHtml = (value) => {
  const html = marked.parse(normalizeMarkdown(value).replace(/\n/g, '<br/>'))
  return DOMPurify.sanitize(html)
}

export const MarkdownTextAreaElement = ({
  value,
  onChange,
  onBlur,
  disabled = false,
  height = '200px',
  className,
}) => {
  const turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
  })

// Add support for underline
turndownService.addRule('underline', {
  filter: ['u'],
  replacement: function (content) {
    return `<u>${content}</u>`
  },
})

// Add support for strikethrough
turndownService.addRule('strikethrough', {
  filter: ['s', 'del', 'strike'],
  replacement: function (content) {
    return `~~${content}~~`
  },
})

// Convert <p> into single newlines instead of double
turndownService.addRule('paragraph', {
  filter: 'p',
  replacement: function (content) {
    return content ? content + '\n' : ''
  },
})

  const quillRef = useRef(null)

  const [editorValue, setEditorValue] = useState(convertMarkdownToHtml(value))

  const handleChange = (newHtml) => {
    setEditorValue(newHtml)
    // Quill emits a <p> per line (and an empty <p><br></p> for blank lines),
    // which Turndown serialises with trailing-space hard breaks and blank
    // lines. Normalise to tight single newlines (see normalizeMarkdown).
    const markdown = normalizeMarkdown(turndownService.turndown(newHtml))
    onChange?.(markdown)
  }

  return (
    <div
      className={twMerge(
        classNames(
          'relative w-full border rounded-md transition duration-200',
          {
            'border-gray-300': !disabled,
            'opacity-50 cursor-not-allowed': disabled,
          },
          className,
          'markdown-editor-wrapper'
        )
      )}
      style={{ '--editor-min-height': height }}
    >
      <ReactQuill
        ref={quillRef}
        value={editorValue}
        onChange={handleChange}
        onBlur={onBlur}
        readOnly={disabled}
        theme="snow"
        modules={modules}
        formats={formats}
      />
    </div>
  )
}

export const MarkdownTextArea = (props) => (
  <Field {...props} element={MarkdownTextAreaElement} />
)

export default MarkdownTextArea
